import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';
import { ListingLink } from '../../components';
import config from '../../config';
import { EditListingPricingForm } from '../../forms';
import { ensureOwnListing } from '../../util/data';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { LISTING_STATE_DRAFT } from '../../util/types';
import css from './EditListingPricingPanel.css';

const { Money } = sdkTypes;

const EditListingPricingPanel = memo(
  props => {
    const {
      className,
      rootClassName,
      listing,
      disabled,
      ready,
      onSubmit,
      onChange,
      submitButtonText,
      panelUpdated,
      updateInProgress,
      errors,
      submitButtonId,
    } = props;

    const classes = classNames(rootClassName || css.root, className);
    const currentListing = ensureOwnListing(listing);
    const { price, publicData } = currentListing.attributes;
    const {
      pricing = {},
      longTermRental = false,
      delivery = false,
      isPeakPriceEnabled = false,
      longTermAdminVerified = false,
      longTermPrice,
      isPublicHolidayPeakDays = false,
      isConsiderPublicHolidaysForPeak = true,
      brandName,
      modelName,
    } = publicData;

    const isPublished =
      currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
    const panelTitle = isPublished ? (
      <FormattedMessage
        id="EditListingPricingPanel.title"
        values={{ listingTitle: <ListingLink listing={listing} /> }}
      />
    ) : (
      <FormattedMessage id="EditListingPricingPanel.createListingTitle" />
    );
    const ensurePricing = useMemo(() => {
      const { peakPrice, discount = {}, ...rest } = pricing;
      return cloneDeep({
        regularDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
        peakDays: ['sun', 'sat'],
        isConsiderPublicHolidaysForPeak,
        isPublicHolidayPeakDays,
        isPeakPriceEnabled,
        ...rest,
        peakPrice:
          peakPrice && peakPrice.amount > 0
            ? new Money(peakPrice.amount, peakPrice.currency)
            : null,
        ...discount,
      });
    }, [pricing]);

    const priceCurrencyValid = price instanceof Money ? price.currency === config.currency : true;
    const longTermPriceMaybe = longTermPrice
      ? { longTermPrice: new Money(longTermPrice.amount, longTermPrice.currency) }
      : {};

    const form = priceCurrencyValid ? (
      <EditListingPricingForm
        className={css.form}
        initialValues={{
          price,
          ...ensurePricing,
          longTermRental,
          delivery,
          longTermAdminVerified,
          isPublicHolidayPeakDays,
          ...longTermPriceMaybe,
          isPeakPriceEnabled,
          brandName,
          modelName,
        }}
        onSubmit={values => {
          const {
            price,
            regularDays,
            peakDays,
            peakPrice,
            week = 0,
            month = 0,
            longTermRental = false,
            longTermPrice = null,
            isConsiderPublicHolidaysForPeak = true,
            isPublicHolidayPeakDays = false,
            isPeakPriceEnabled = values.peakPrice.amount &&
            values.isPublicHolidayPeakDays &&
            values.regularDays.length > 0
              ? true
              : false,
            delivery = false,
          } = values;
          const existingPricing = pricing || {};
          const existingDiscount = existingPricing.discount || {};
          
          const data = {
            price,
            publicData: {
              isConsiderPublicHolidaysForPeak,
              isPeakPriceEnabled,
              isPublicHolidayPeakDays: !isPeakPriceEnabled ? false : isPublicHolidayPeakDays,
              longTermRental,
              delivery,
              longTermPrice: longTermPrice
                ? pick(longTermPrice, ['amount', 'currency'])
                : { amount: 0, currency: config.currency },
              longTermPriceAmount: longTermPrice ? longTermPrice.amount : 0,
              pricing: {
                ...existingPricing,
                regularDays: !isPeakPriceEnabled
                  ? ['tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'mon']
                  : regularDays,
                peakDays: !isPeakPriceEnabled ? [] : peakDays,
                peakPrice: peakPrice
                  ? {
                      amount: peakPrice.amount,
                      currency: peakPrice.currency,
                    }
                  : {
                      amount: 0,
                      currency: config.currency,
                    },
                discount: {
                  ...existingDiscount,
                  week,
                  month,
                },
              },
            },
          };
          onSubmit(data);
        }}
        listing={listing}
        onChange={onChange}
        saveActionMsg={submitButtonText}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
        submitButtonId={submitButtonId}
      />
    ) : (
      <div className={css.priceCurrencyInvalid}>
        <FormattedMessage id="EditListingPricingPanel.listingPriceCurrencyInvalid" />
      </div>
    );
    const title = <FormattedMessage id="EditListingPricingPanel.panelTitle" />;
    const panelDescription = <FormattedMessage id="EditListingPricingPanel.panelDescription" />;
    return (
      <div className={classes}>
        <div className={css.titleSection}>
          <h1 className={css.title}>{title}</h1>
          <p>{panelDescription}</p>
        </div>
        {form}
      </div>
    );
  },
  (prev, next) =>
    isEqual(
      pick(prev, ['listing', 'updateInProgress', 'panelUpdated']),
      pick(next, ['listing', 'updateInProgress', 'panelUpdated'])
    )
);

const { func, object, string, bool } = PropTypes;

EditListingPricingPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingPricingPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
  submitButtonId: string,
};

export default EditListingPricingPanel;
